import { SearchFormStoreFactory } from "@smartrr/shared/components/SearchFormStoreFactory";
import { QuickbuyAPI } from "@smartrr/shared/interfaces/quickbuy/api";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { create } from "zustand";

import { SmartrrApi } from "@customer-app/api";
import { StatusCodes } from "http-status-codes";

const PRODUCTS_PER_PAGE = 10;
export const QuickbuyProductSearch = SearchFormStoreFactory<
  {
    perPage: number;
  },
  QuickbuyAPI.Product.Type
>(
  { perPage: PRODUCTS_PER_PAGE },
  async ({ perPage }, page, search) => {
    const productsResponse = await SmartrrApi.quickBuy.products.getProducts({
      query: {
        pageNumber: page,
        pageSize: perPage,
        productName: search,
      },
    });

    if (productsResponse.status === StatusCodes.OK) {
      return {
        count: productsResponse.body.count,
        data: productsResponse.body.data,
      };
    }

    return {
      count: 0,
      data: [],
    };
  },
  {
    scrollType: "infinite",
    getId: p => p.shopifyId,
  }
);

interface QuickbuyStore {
  product: QuickbuyAPI.Product.Type | null;
  variant: QuickbuyAPI.Variant.Type | null;
  quantity: number;

  actions: {
    updateProduct(product: QuickbuyAPI.Product.Type | null): void;
    updateVariant(variant: QuickbuyAPI.Variant.Type | null): void;
    updateQuantity(newQuantity: number): void;
    buy(): void;
  };
}

const useQuickbuyStore = create<QuickbuyStore>()((set, get) => ({
  product: null,
  variant: null,
  quantity: 1,
  actions: {
    updateProduct(product: QuickbuyAPI.Product.Type | null): void {
      if (!product) {
        set({ product: null, variant: null, quantity: 1 });
        return;
      }
      if (product.variants.length) {
        set({ product, variant: product.variants[0] });
      }
    },
    updateVariant(variant: QuickbuyAPI.Variant.Type | null): void {
      const product = get().product;
      if (product === null) {
        return;
      }
      if (variant === null) {
        return;
      }
      if (product.variants.find(v => v.shopifyId === variant.shopifyId) !== undefined) {
        set({ variant });
      }
    },
    updateQuantity(newQuantity: number): void {
      if (get().product && get().variant) {
        set({
          quantity: Math.max(1, newQuantity),
        });
      }
    },
    buy(): void {
      const product = get().product;
      const variant = get().variant;
      const quantity = get().quantity;
      if (!product || !variant || quantity < 1) {
        return;
      }

      window.open(
        `/cart/add?id=${shopifyGidToNumber(variant.shopifyId)}&quantity=${quantity}&return_to=/checkout`
      );

      set({ product: null, variant: null, quantity: 1 });
    },
  },
}));

const initialState = useQuickbuyStore.getState();

export const QuickbuyStoreAccess = {
  useActions: () => useQuickbuyStore(state => state.actions),
  useQuantity: () => useQuickbuyStore(state => state.quantity),
  useSelectedProduct: () => useQuickbuyStore(state => state.product),
  useSelectedVariant: () => useQuickbuyStore(state => state.variant),
  testing: {
    state: useQuickbuyStore.getState,
    actions: useQuickbuyStore.getState().actions,
    reset: () => useQuickbuyStore.setState(initialState),
  },
};
